import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useStore } from '../store/useStore'
import Layout from '../components/Layout'
import { api } from '../lib/api'
import MatchSettingsModal from '../components/MatchSettingsModal'
import { useAlert } from '../components/AlertProvider'

interface Match {
  id: number
  user_id_1: number
  user_id_2: number
  status: 'pending' | 'matched' | 'rejected'
  match_score: number
  matched_at: string | null
  created_at: string
  other_user?: {
    id: number
    username: string
    picture: string | null
    locale: string
    has_persona?: number
  }
  last_message?: {
    content: string
    content_translated: string | null
    sender_id: number
    created_at: string
    sent_by: 'user' | 'ai_avatar'
  }
  unread_count?: number
  is_ai_chatting?: boolean
  engagement_score?: number
  engagement_label?: string
}

interface Suggestion {
  id: number
  batchMatchId: number
  suggestedUserId: number
  matchScore: number
  reason: string
  otherUser: {
    id: number
    username: string
    picture: string | null
    displayName: string | null
    country: string | null
    gender: string | null
    mbti: string | null
    interests: string[]
    bio: string | null
    locale: string
    has_persona?: number
  }
}

type SortBy = 'score' | 'recent'

// 시간 포맷
function formatTime(dateStr: string, t: any): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const hours = Math.floor(diff / (1000 * 60 * 60))
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))

  if (hours < 1) return t('matches.justNow')
  if (hours < 24) return t('matches.hoursAgo', { count: hours })
  if (days < 7) return t('matches.daysAgo', { count: days })
  return date.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })
}

export default function MatchesPage() {
  const { user } = useStore()
  const { t } = useTranslation()
  const { showAlert } = useAlert()
  const [matches, setMatches] = useState<Match[]>([])
  const [suggestion, setSuggestion] = useState<Suggestion | null>(null)
  const [loading, setLoading] = useState(true)
  const [respondingToSuggestion, setRespondingToSuggestion] = useState(false)
  const [filter, setFilter] = useState<'all' | 'ai' | 'me'>('all')
  const [sortBy, setSortBy] = useState<SortBy>('score')
  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    fetchMatches(sortBy)
    fetchTodaysSuggestion()
  }, [sortBy])

  const fetchMatches = async (sort: SortBy) => {
    try {
      setLoading(true)
      const data = await api.matches.list(sort)
      setMatches((data.matches || []) as Match[])
    } catch (error) {
      console.error('Failed to fetch matches:', error)
      setMatches([])
    } finally {
      setLoading(false)
    }
  }

  const fetchTodaysSuggestion = async () => {
    try {
      const data = await api.matches.getSuggestion()
      if (data.suggestion) {
        setSuggestion(data.suggestion as Suggestion)
      }
    } catch (error) {
      console.error('Failed to fetch suggestion:', error)
      setSuggestion(null)
    }
  }

  const handleRespondToSuggestion = async (response: 'accept' | 'reject') => {
    if (!suggestion) return

    try {
      setRespondingToSuggestion(true)
      const result = await api.matches.respondToSuggestion(suggestion.id, response)

      if (result.mutualAccept && result.finalMatchId) {
        await showAlert(t('matches.matchSuccess'))
        window.location.href = `/chat/${result.finalMatchId}`
      } else {
        if (response === 'accept') {
          showAlert(t('matches.matchSuccess'))
        } else {
          showAlert(t('matches.rejectSuccess'))
        }
        setSuggestion(null)
        setTimeout(() => {
          fetchTodaysSuggestion()
        }, 1000)
      }
    } catch (error) {
      console.error('Failed to respond to suggestion:', error)
      showAlert(t('common.error'))
    } finally {
      setRespondingToSuggestion(false)
    }
  }

  const filteredMatches = matches.filter((match) => {
    if (filter === 'ai') return match.is_ai_chatting
    if (filter === 'me') return !match.is_ai_chatting && match.status === 'matched'
    return true
  })

  const matchedCount = matches.filter((m) => m.status === 'matched').length
  const aiChattingCount = matches.filter((m) => m.is_ai_chatting).length

  return (
    <Layout>
      <style>{`
        @keyframes pulse-glow {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.1); }
        }
      `}</style>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gradient-premium">
              {t('nav.matches')}
            </h1>
            <p className="text-sm text-base-content/60 mt-1">
              {t('matches.chatting', { count: matchedCount })}
              {aiChattingCount > 0 && ` · ${t('matches.aiChattingCount', { count: aiChattingCount })}`}
            </p>
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className="btn btn-ghost btn-circle glass"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.438.995s.145.755.438.995l1.003.827c.424.35.534.954.26 1.431l-1.296 2.247a1.125 1.125 0 01-1.37.49l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.063-.374-.313-.686-.645-.87a6.52 6.52 0 01-.22-.127c-.324-.196-.72-.257-1.075-.124l-1.217.456a1.125 1.125 0 01-1.37-.49l-1.296-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.437-.995s-.145-.755-.437-.995l-1.004-.827a1.125 1.125 0 01-.26-1.431l1.296-2.247a1.125 1.125 0 011.37-.49l1.217.456c.355.133.75.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        </div>

        <MatchSettingsModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />

        {/* Today's Suggestion Card */}
        {suggestion && (
          <div className="suggestion-card mb-6 fade-in-scale">
            <div className="relative z-10">
              {/* Badge */}
              <div className="text-center mb-4">
                <span className="badge-premium inline-flex items-center gap-1.5 px-4 py-1.5">
                  <span className="text-base">✨</span>
                  {t('matches.todaySuggestion')}
                </span>
              </div>

              {/* Profile */}
              <Link to={`/profile/${suggestion.otherUser.id}`} className="block">
                <div className="flex flex-col items-center">
                  <div className="relative mb-4">
                    <div className="avatar-premium avatar-ring-animated">
                      <div className="w-28 h-28 rounded-full overflow-hidden bg-gradient-to-br from-coral-200 to-mint-200">
                        {suggestion.otherUser.picture ? (
                          <img src={suggestion.otherUser.picture} alt={suggestion.otherUser.username} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-white text-4xl font-bold">
                            {suggestion.otherUser.username?.[0]?.toUpperCase() || '?'}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Persona indicator - blue pulse (replaces country flag) */}
                    {suggestion.otherUser.has_persona === 1 && (
                      <div
                        className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-white"
                        style={{
                          background: '#3b82f6',
                          boxShadow: '0 0 10px rgba(59, 130, 246, 0.7)',
                          animation: 'pulse-glow 2s ease-in-out infinite',
                        }}
                        title="AI 자동응답 활성화됨"
                      />
                    )}
                  </div>

                  <h2 className="text-xl font-bold text-base-content">
                    {suggestion.otherUser.displayName || suggestion.otherUser.username}
                  </h2>
                  {/* reason은 국가 힌트가 포함되어 있으므로 숨김 - 스테이지 1에서는 국가 비공개 */}

                  {suggestion.otherUser.bio && (
                    <p className="text-sm text-base-content/50 italic mt-2 text-center max-w-[280px]">
                      "{suggestion.otherUser.bio}"
                    </p>
                  )}

                  {/* Tags */}
                  <div className="flex flex-wrap justify-center gap-2 mt-4">
                    {suggestion.otherUser.mbti && (
                      <span className="badge badge-outline border-primary/30 text-primary">
                        {suggestion.otherUser.mbti}
                      </span>
                    )}
                    {suggestion.otherUser.interests.slice(0, 3).map((interest, idx) => (
                      <span key={idx} className="badge badge-ghost">
                        {interest}
                      </span>
                    ))}
                  </div>

                  {/* Match Score */}
                  <div className="mt-5 w-full max-w-[200px]">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-base-content/50">{t('matches.matchScore')}</span>
                      <span className="text-xl font-bold text-gradient-coral">
                        {Math.round(suggestion.matchScore)}%
                      </span>
                    </div>
                    <div className="progress-premium">
                      <div
                        className="progress-premium-bar"
                        style={{ width: `${suggestion.matchScore}%` }}
                      />
                    </div>
                  </div>
                </div>
              </Link>

              {/* Action Buttons */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => handleRespondToSuggestion('reject')}
                  disabled={respondingToSuggestion}
                  className="flex-1 btn btn-outline rounded-full h-12 border-2 hover:bg-base-200"
                >
                  {respondingToSuggestion ? (
                    <span className="loading loading-spinner loading-sm" />
                  ) : (
                    <>
                      <span className="text-lg mr-1">👋</span>
                      {t('matches.reject')}
                    </>
                  )}
                </button>
                <button
                  onClick={() => handleRespondToSuggestion('accept')}
                  disabled={respondingToSuggestion}
                  className="flex-1 btn-premium h-12"
                >
                  {respondingToSuggestion ? (
                    <span className="loading loading-spinner loading-sm" />
                  ) : (
                    <>
                      <span className="text-lg mr-1">💕</span>
                      {t('matches.accept')}!
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="flex bg-base-200/50 rounded-2xl p-1 mb-4">
          <button
            className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-medium transition-all ${filter === 'all' ? 'bg-white text-primary shadow-sm' : 'text-base-content/60'
              }`}
            onClick={() => setFilter('all')}
          >
            {t('matches.allTab')}
          </button>
          <button
            className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-medium transition-all ${filter === 'ai' ? 'bg-white text-secondary shadow-sm' : 'text-base-content/60'
              }`}
            onClick={() => setFilter('ai')}
          >
            {t('matches.aiChatting')}
          </button>
          <button
            className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-medium transition-all ${filter === 'me' ? 'bg-white text-accent shadow-sm' : 'text-base-content/60'
              }`}
            onClick={() => setFilter('me')}
          >
            {t('matches.myChatsTab')}
          </button>
        </div>

        {/* Sort Toggle */}
        <div className="flex items-center justify-end gap-2 mb-4">
          <span className="text-xs text-base-content/50">{t('matches.sort')}:</span>
          <div className="flex bg-base-200/50 rounded-xl p-0.5">
            <button
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${sortBy === 'score' ? 'bg-white shadow-sm text-primary' : 'text-base-content/60'
                }`}
              onClick={() => setSortBy('score')}
            >
              {t('matches.sortByScore')}
            </button>
            <button
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${sortBy === 'recent' ? 'bg-white shadow-sm text-primary' : 'text-base-content/60'
                }`}
              onClick={() => setSortBy('recent')}
            >
              {t('matches.sortByRecent')}
            </button>
          </div>
        </div>

        {/* Match List */}
        {loading ? (
          <div className="flex justify-center py-16">
            <span className="loading loading-spinner loading-lg text-primary" />
          </div>
        ) : filteredMatches.length === 0 ? (
          <div className="glass-card p-8 text-center fade-in-up">
            <div className="text-6xl mb-4">💝</div>
            <h3 className="text-lg font-bold text-base-content mb-2">
              {t('matches.noMatches')}
            </h3>
            <p className="text-sm text-base-content/60 mb-4">
              {t('matches.completeProfile')}
            </p>
            <Link to="/profile" className="btn-premium inline-flex items-center gap-2 px-6">
              {t('matches.completeProfileBtn')}
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredMatches.map((match, index) => (
              <Link
                key={match.id}
                to={match.status === 'matched' ? `/chat/${match.id}` : '#'}
                className={`match-card block fade-in-up ${match.status !== 'matched' ? 'opacity-60' : ''}`}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="flex items-center gap-3">
                  {/* Avatar */}
                  <div className="relative flex-shrink-0">
                    <div className="w-14 h-14 rounded-2xl overflow-hidden bg-gradient-to-br from-coral-200 to-mint-200">
                      {match.other_user?.picture ? (
                        <img src={match.other_user.picture} alt={match.other_user.username} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white text-xl font-bold">
                          {match.other_user?.username?.[0]?.toUpperCase() || '?'}
                        </div>
                      )}
                    </div>
                    {/* Persona indicator - blue pulse (replaces country flag) */}
                    {match.other_user?.has_persona === 1 && (
                      <div
                        className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white"
                        style={{
                          background: '#3b82f6',
                          boxShadow: '0 0 8px rgba(59, 130, 246, 0.6)',
                          animation: 'pulse-glow 2s ease-in-out infinite',
                        }}
                        title="AI 자동응답 활성화됨"
                      />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-base-content truncate">
                        {match.other_user?.username}
                      </span>
                      {match.is_ai_chatting && (
                        <span className="badge badge-xs bg-secondary/20 text-secondary border-0">
                          🤖 AI
                        </span>
                      )}
                      {match.status === 'pending' && (
                        <span className="badge badge-xs badge-ghost">{t('matches.pending')}</span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs font-medium text-primary">
                        {match.match_score}%
                      </span>
                      {match.engagement_label && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${match.engagement_label.includes('높음') || match.engagement_label.includes('High')
                            ? 'bg-success/15 text-success'
                            : match.engagement_label.includes('중간') || match.engagement_label.includes('Medium')
                              ? 'bg-warning/15 text-warning'
                              : 'bg-base-200 text-base-content/50'
                          }`}>
                          {match.engagement_label}
                        </span>
                      )}
                    </div>

                    {match.last_message && (
                      <p className="text-sm text-base-content/50 truncate mt-1">
                        {match.last_message.sender_id === user?.id && (
                          <span className="text-primary">{t('matches.mePrefix')}</span>
                        )}
                        {match.last_message.content_translated || match.last_message.content}
                      </p>
                    )}
                  </div>

                  {/* Right */}
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    {match.last_message && (
                      <span className="text-xs text-base-content/40">
                        {formatTime(match.last_message.created_at, t)}
                      </span>
                    )}
                    {(match.unread_count || 0) > 0 && (
                      <span className="badge-premium text-xs px-2 py-0.5">
                        {match.unread_count}
                      </span>
                    )}
                  </div>
                </div>

                {/* AI Chatting Info */}
                {match.is_ai_chatting && match.status === 'matched' && (
                  <div className="mt-3 pt-3 border-t border-base-200/80">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-base-content/50 flex items-center gap-1">
                        <span>🤖</span>
                        {t('matches.aiResponding')}
                      </span>
                      <button
                        className="btn btn-xs btn-outline btn-primary rounded-full"
                        onClick={(e) => {
                          e.preventDefault()
                        }}
                      >
                        {t('matches.takeOverChat')}
                      </button>
                    </div>
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </Layout>
  )
}
